import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Camera, Upload, CreditCard, DollarSign, Zap,
  ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, User,
  ChevronRight, Music, Radio, Video, Calendar, Headphones
} from 'lucide-react';
import AvatarPickerModal from './AvatarPickerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ONBOARDING_KEY = 'onboarding_flow_dismissed';
const ONBOARDING_SNOOZE_KEY = 'onboarding_flow_snoozed_until';
const TOUR_STORAGE_KEY = 'onevoice_tour_seen';

// Routes where the onboarding modal must NEVER auto-pop. These are
// either purchase-funnel pages (any blocking modal kills conversion)
// or read-only marketing pages where the user explicitly came to read.
// Keep this list short — the regular trigger fires on every other page.
const SUPPRESSED_PATH_PREFIXES = [
  '/vip',
  '/paid-to-listen',
  '/paid-to-listen/info',
  '/paid-to-listen-info',
  '/checkout',
  '/promote',
  '/cart',
  '/payment',
  '/stripe',
  // OneVoice Studio is a focused creative surface — recording / beat-making
  // sessions get derailed if a multi-step welcome dialog blocks the DAW.
  // The onboarding still appears on /home and /dashboard so the user
  // never misses the tour, just not while they're trying to lay vocals.
  '/studio',
];

export default function OnboardingFlow() {
  const { user, token, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  // Hard kill-switch: once the user dismisses or snoozes this session,
  // never re-show even if the parent useEffect refires (fixes a regression
  // where rapid user-object identity changes from AuthContext were re-mounting
  // the modal immediately after a snooze click).
  const dismissedThisSession = useRef(false);

  // Photo state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [photoUploaded, setPhotoUploaded] = useState(false);

  // Stripe state
  const [stripeStatus, setStripeStatus] = useState(null);

  const canReceivePayouts = user?.is_artist || user?.is_producer || user?.is_ai_artist || user?.is_reviewer;

  useEffect(() => {
    if (!isAuthenticated || !token || !user) {
      setLoading(false);
      return;
    }
    // Hard guard — once dismissed this session, stay dismissed.
    if (dismissedThisSession.current) {
      setLoading(false);
      return;
    }
    // Suppress on critical conversion / read-only routes — never block
    // a paying user from reaching their checkout button.
    const path = location.pathname || '';
    if (SUPPRESSED_PATH_PREFIXES.some(p => path === p || path.startsWith(p + '/'))) {
      setShow(false);
      setLoading(false);
      return;
    }
    checkOnboardingNeeds();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, [isAuthenticated, token, user, location.pathname]);

  const checkOnboardingNeeds = async () => {
    // Admin users never need onboarding
    if (user?.is_admin) {
      setLoading(false);
      return;
    }

    const now = Date.now();

    // Check snooze
    const snoozedUntil = localStorage.getItem(ONBOARDING_SNOOZE_KEY);
    if (snoozedUntil && now < parseInt(snoozedUntil)) {
      setLoading(false);
      return;
    }

    // Check daily dismiss
    const lastDismissed = localStorage.getItem(ONBOARDING_KEY);
    if (lastDismissed) {
      const hoursSince = (now - parseInt(lastDismissed)) / (1000 * 60 * 60);
      if (hoursSince < 24) {
        setLoading(false);
        return;
      }
    }

    const neededSteps = [];

    // Step 1: Welcome tour (show if not seen)
    const tourData = localStorage.getItem(TOUR_STORAGE_KEY);
    let tourSeen = false;
    if (tourData) {
      try {
        const parsed = JSON.parse(tourData);
        if (parsed.expiresAt && new Date(parsed.expiresAt) > new Date()) {
          tourSeen = true;
        }
      } catch {}
    }
    if (!tourSeen) {
      neededSteps.push('welcome');
    }

    // Step 2: Profile photo
    if (!user.profile_picture) {
      neededSteps.push('photo');
    }

    // Step 3: Stripe setup (only for creators)
    if (canReceivePayouts) {
      try {
        const res = await axios.get(`${API}/stripe-connect/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStripeStatus(res.data);
        if (res.data?.status !== 'active') {
          neededSteps.push('stripe');
        }
      } catch {}
    }

    if (neededSteps.length > 0) {
      setSteps(neededSteps);
      setStep(0);
      setShow(true);
    }
    setLoading(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(ONBOARDING_KEY, Date.now().toString());
    // Mark tour as seen too
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 30);
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify({ seen: true, expiresAt: expiry.toISOString() }));
    dismissedThisSession.current = true;
    setShow(false);
  };

  const handleSnooze = () => {
    const snoozeUntil = Date.now() + (7 * 24 * 60 * 60 * 1000);
    localStorage.setItem(ONBOARDING_SNOOZE_KEY, snoozeUntil.toString());
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify({ seen: true, expiresAt: new Date(snoozeUntil).toISOString() }));
    dismissedThisSession.current = true;
    setShow(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUploadPhoto = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      await axios.post(`${API}/users/profile/picture`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile picture uploaded!');
      setPhotoUploaded(true);
      if (refreshUser) await refreshUser();
      setTimeout(() => handleNext(), 1500);
    } catch {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleSetupStripe = () => {
    handleDismiss();
    navigate('/dashboard');
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleDismiss();
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  if (loading || !show || steps.length === 0) return null;

  const currentStep = steps[step];
  const totalSteps = steps.length;

  const stepLabels = { welcome: 'Welcome', photo: 'Profile Photo', stripe: 'Payments' };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div
        className="dark-surface relative w-full max-w-md bg-[#0a0a0f] border border-white/[0.08] rounded-xl overflow-hidden shadow-2xl"
        data-testid="onboarding-flow"
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-3 flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold text-sm">
              {totalSteps > 1 ? `Step ${step + 1}: ${stepLabels[currentStep]}` : stepLabels[currentStep]}
            </h2>
            <p className="text-white/25 text-[10px] font-mono mt-0.5">
              {step + 1} of {totalSteps}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/20 hover:text-white/60 transition-colors p-1"
            data-testid="onboarding-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {/* Progress */}
        <div className="flex gap-1 px-5 pb-3">
          {steps.map((_, i) => (
            <div
              key={i}
              className="h-1 flex-1 rounded-full transition-all duration-300"
              style={{
                background: i <= step ? '#00CCFF' : 'rgba(255,255,255,0.06)',
                boxShadow: i === step ? '0 0 8px rgba(0,204,255,0.3)' : 'none'
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="px-5 pb-2">
          {currentStep === 'welcome' && <WelcomeStep />}
          {currentStep === 'photo' && (
            <PhotoStep
              previewUrl={previewUrl}
              photoUploaded={photoUploaded}
              uploading={uploading}
              onFileSelect={handleFileSelect}
              onUpload={handleUploadPhoto}
              selectedFile={selectedFile}
              onGalleryDone={async () => {
                // Avatar picked from gallery — treat as photo step complete.
                setPhotoUploaded(true);
                try { await refreshUser?.(); } catch {}
                setTimeout(() => handleNext(), 800);
              }}
            />
          )}
          {currentStep === 'stripe' && (
            <StripeStep stripeStatus={stripeStatus} onSetup={handleSetupStripe} />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-4 pt-2 space-y-2">
          <div className="flex gap-2 items-center">
            {step > 0 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-white/25 hover:text-white/50 text-xs font-mono transition-colors"
                data-testid="onboarding-back-btn"
              >
                <ArrowLeft size={13} /> Back
              </button>
            )}
            <div className="flex-1" />

            {currentStep === 'welcome' && (
              <Button
                onClick={handleNext}
                className="bg-[#00CCFF] hover:bg-[#00CCFF]/85 text-black font-semibold rounded-lg text-sm px-5"
                data-testid="onboarding-next-btn"
              >
                {steps.length > 1 ? 'Continue' : 'Got it'}
                <ChevronRight size={15} className="ml-1" />
              </Button>
            )}

            {currentStep === 'photo' && !photoUploaded && (
              <button
                onClick={handleNext}
                className="text-white/25 hover:text-white/50 font-mono text-xs transition-colors"
                data-testid="onboarding-skip-btn"
              >
                Skip <ChevronRight size={13} className="inline ml-0.5" />
              </button>
            )}
          </div>

          <button
            onClick={handleSnooze}
            className="w-full text-center text-white/15 hover:text-white/30 font-mono text-[10px] transition-colors py-1"
            data-testid="onboarding-snooze-btn"
          >
            Don't show for 7 days
          </button>
        </div>
      </div>
    </div>
  );
}

function WelcomeStep() {
  const { user } = useAuth();
  const isArtist = user?.is_artist;
  const isProducer = user?.is_producer;
  const isAiArtist = user?.is_ai_artist;
  const isDJ = user?.is_dj;
  const isPromoter = user?.is_promoter;
  const isAdvertiser = user?.is_advertiser;
  const isCreator = isArtist || isProducer || isAiArtist;

  // Role-specific features
  const getRoleFeatures = () => {
    if (isDJ) return [
      { icon: Radio, label: 'Browse Radio Stations', desc: 'Music, Beats & AI Radio', color: '#10b981' },
      { icon: Music, label: 'Create Mood Playlists', desc: 'Curate themed sets', color: '#a855f7' },
      { icon: Headphones, label: 'Live Sessions', desc: 'Book & host live DJ sets', color: '#f43f5e' },
      { icon: Calendar, label: 'Gig Marketplace', desc: 'Find performance gigs', color: '#f59e0b' },
    ];
    if (isPromoter) return [
      { icon: Calendar, label: 'Create Events', desc: 'Promote shows & concerts', color: '#f59e0b' },
      { icon: Music, label: 'Gig Marketplace', desc: 'Post opportunities for artists', color: '#6c5ce7' },
      { icon: Headphones, label: 'Discover Talent', desc: 'Scout artists & DJs', color: '#a855f7' },
      { icon: Video, label: 'Analytics', desc: 'Track event & ticket sales', color: '#10b981' },
    ];
    if (isAdvertiser) return [
      { icon: Video, label: 'Create Ad Campaigns', desc: 'Video & audio ads', color: '#f43f5e' },
      { icon: Radio, label: 'Target Radios', desc: 'Music, Beats & AI stations', color: '#10b981' },
      { icon: Calendar, label: 'Schedule Ads', desc: 'Set start & end dates', color: '#f59e0b' },
      { icon: Headphones, label: 'Track Performance', desc: 'Impressions, clicks & CPM', color: '#a855f7' },
    ];
    if (isCreator) return [
      { icon: Upload, label: 'Upload Your Music', desc: isAiArtist ? 'Share AI-generated tracks' : isProducer ? 'Upload beats & productions' : 'Share tracks & albums', color: '#10b981' },
      { icon: Video, label: 'Music Videos', desc: 'Upload free & premium content', color: '#f43f5e' },
      { icon: Headphones, label: 'Get Discovered', desc: 'Appear on radio & browse', color: '#a855f7' },
      { icon: Calendar, label: 'Earn Money', desc: 'Sell tracks, beats & licenses', color: '#f59e0b' },
    ];
    // Fan
    return [
      { icon: Radio, label: 'Free Radio Stations', desc: 'Music, Beats & AI Radio', color: '#10b981' },
      { icon: Video, label: 'Music Videos', desc: 'Free & premium content', color: '#f43f5e' },
      { icon: Headphones, label: 'Discover Artists', desc: 'Follow & support creators', color: '#a855f7' },
      { icon: Calendar, label: 'Live Events', desc: 'Concerts & shows', color: '#f59e0b' },
    ];
  };

  const getRoleTitle = () => {
    if (isDJ) return 'Welcome, DJ!';
    if (isPromoter) return 'Welcome, Promoter!';
    if (isAdvertiser) return 'Welcome, Advertiser!';
    if (isAiArtist) return 'Welcome, AI Artist!';
    if (isProducer) return 'Welcome, Producer!';
    if (isArtist) return 'Welcome, Artist!';
    return 'Welcome to One Voice';
  };

  const getRoleSubtitle = () => {
    if (isDJ) return 'Curate playlists, host live sessions & get booked';
    if (isPromoter) return 'Promote events, discover talent & grow the scene';
    if (isAdvertiser) return 'Run ad campaigns across radio & video';
    if (isCreator) return 'Upload, sell & grow your audience';
    return 'The home for independent music';
  };

  const features = getRoleFeatures();

  return (
    <div className="space-y-4" data-testid="onboarding-welcome-step">
      <div className="text-center pb-1">
        <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-[#00CCFF]/10 border border-[#00CCFF]/20 flex items-center justify-center">
          <Music size={26} className="text-[#00CCFF]" />
        </div>
        <h3 className="text-white font-semibold text-lg">{getRoleTitle()}</h3>
        <p className="text-white/35 text-xs font-mono mt-1">{getRoleSubtitle()}</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {features.map(({ icon: Icon, label, desc, color }) => (
          <div
            key={label}
            className="p-3 bg-white/[0.02] border border-white/[0.05] rounded-lg"
          >
            <Icon size={16} style={{ color }} className="mb-1.5" />
            <p className="text-white/80 text-xs font-medium">{label}</p>
            <p className="text-white/25 text-[10px] font-mono mt-0.5">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhotoStep({ previewUrl, photoUploaded, uploading, onFileSelect, onUpload, selectedFile, onGalleryDone }) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  return (
    <div className="space-y-4" data-testid="onboarding-photo-step">
      <div>
        <h3 className="text-white font-semibold mb-1">Add a Profile Photo</h3>
        <p className="text-white/35 text-xs font-mono">Help fans and collaborators recognize you</p>
      </div>

      {photoUploaded ? (
        <div className="flex flex-col items-center py-6">
          <div className="w-14 h-14 rounded-full bg-[#10b981]/15 flex items-center justify-center mb-2">
            <CheckCircle size={28} className="text-[#10b981]" />
          </div>
          <p className="text-[#10b981] font-semibold text-sm">Photo uploaded!</p>
          <p className="text-white/25 text-[10px] font-mono mt-1">Moving to next step...</p>
        </div>
      ) : (
        <>
          <label className="block cursor-pointer" htmlFor="onboarding-photo-input">
            <div className={`border-2 border-dashed rounded-xl ${previewUrl ? 'border-[#00CCFF]/40' : 'border-white/[0.08] hover:border-white/[0.12]'} transition-colors p-5 flex flex-col items-center`}>
              {previewUrl ? (
                <div className="relative">
                  <img src={previewUrl} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-[#00CCFF]" />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#00CCFF] rounded-full flex items-center justify-center">
                    <Camera size={12} className="text-black" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-12 h-12 bg-white/[0.03] rounded-full flex items-center justify-center mb-2">
                    <Upload size={20} className="text-white/15" />
                  </div>
                  <p className="text-white/35 text-sm">Click to upload</p>
                  <p className="text-white/15 text-[10px] font-mono mt-0.5">JPG, PNG, GIF (max 5MB)</p>
                </>
              )}
            </div>
          </label>
          <input
            id="onboarding-photo-input"
            type="file"
            accept="image/*"
            onChange={onFileSelect}
            className="hidden"
            data-testid="onboarding-photo-input"
          />
          {selectedFile && (
            <Button
              onClick={onUpload}
              disabled={uploading}
              className="w-full bg-[#00CCFF] hover:bg-[#00CCFF]/85 text-black font-semibold rounded-lg"
              data-testid="onboarding-upload-btn"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Uploading...
                </span>
              ) : (
                <span className="flex items-center gap-2"><Upload size={15} /> Upload Photo</span>
              )}
            </Button>
          )}

          {/* OR — pick a hand-illustrated avatar from the gallery */}
          <div className="flex items-center gap-2 my-1">
            <div className="flex-1 h-px bg-white/[0.06]" />
            <span className="text-white/25 font-mono text-[10px] uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-white/[0.06]" />
          </div>
          <button
            type="button"
            onClick={() => setGalleryOpen(true)}
            className="w-full text-center text-[#00CCFF] hover:text-white font-mono text-xs py-1.5 transition-colors"
            data-testid="onboarding-pick-avatar-btn"
          >
            Pick an avatar from the gallery →
          </button>
        </>
      )}

      <AvatarPickerModal
        open={galleryOpen}
        onClose={(picked) => {
          setGalleryOpen(false);
          if (picked) onGalleryDone?.();
        }}
        token={sessionStorage.getItem('token') || localStorage.getItem('token')}
        title="Pick a starter avatar"
        subtitle="Hand-illustrated portraits — you can change or replace it anytime."
      />
    </div>
  );
}

function StripeStep({ stripeStatus, onSetup }) {
  const isIncomplete = stripeStatus?.connected && stripeStatus?.status !== 'active';

  return (
    <div className="space-y-4" data-testid="onboarding-stripe-step">
      <div>
        <h3 className="text-white font-semibold mb-1">
          {isIncomplete ? 'Complete Stripe Setup' : 'Set Up Payments'}
        </h3>
        <p className="text-white/35 text-xs font-mono">
          {isIncomplete ? 'Finish verification to receive payouts' : 'Start receiving instant payouts'}
        </p>
      </div>

      <div className="bg-[#FFDA33]/5 border border-[#FFDA33]/12 rounded-lg p-2.5 flex items-start gap-2.5">
        <AlertTriangle size={14} className="text-[#FFDA33] flex-shrink-0 mt-0.5" />
        <p className="text-white/40 text-[11px] font-mono">
          Without Stripe, card payments require manual withdrawal.
        </p>
      </div>

      <div className="space-y-1.5">
        {[
          { icon: Zap, color: '#FFDA33', text: 'Instant payouts to your bank' },
          { icon: DollarSign, color: '#10b981', text: 'Keep 90% of every sale' },
          { icon: CreditCard, color: '#00CCFF', text: 'No manual withdrawal needed' },
        ].map(({ icon: Icon, color, text }) => (
          <div key={text} className="flex items-center gap-2.5 p-2 bg-white/[0.02] rounded-lg">
            <Icon size={14} style={{ color }} className="flex-shrink-0" />
            <span className="text-white/50 text-xs font-mono">{text}</span>
          </div>
        ))}
      </div>

      <Button
        onClick={onSetup}
        className="w-full bg-[#635BFF] hover:bg-[#5247E6] text-white font-semibold rounded-lg"
        data-testid="onboarding-stripe-btn"
      >
        {isIncomplete ? 'Complete Setup' : 'Set Up Stripe'}
        <ArrowRight size={15} className="ml-2" />
      </Button>
    </div>
  );
}
